import type { CelebrationDraftDto, ChatEventSuggestionDto } from '@om/shared-types';
import type { CreateCelebrationWorkflowInput } from './createCelebrationWorkflow';
import { isValidSubtype } from '@/config/celebrationRegistry';

interface BuildCreateWorkflowInputParams {
  draft: CelebrationDraftDto;
  chatSessionId?: string;
  confirmedTags?: Record<string, string[]>;
  selectedEvents?: ChatEventSuggestionDto[];
  selectedActivities?: Record<string, string[]>;
  selectedGoalTemplateIdsByEvent?: Record<string, number[]>;
  addServicesToShortlist?: boolean;
  selectedServiceDefinitionIds?: number[];
}

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const TEMPLATE_PLACEHOLDER_VALUES = new Set(['default', 'none', 'null', 'undefined']);

export const normalizeWorkflowTemplateKey = (value: unknown): string | undefined => {
  const normalized = toOptionalString(value);
  if (!normalized) {
    return undefined;
  }

  return TEMPLATE_PLACEHOLDER_VALUES.has(normalized.toLowerCase())
    ? undefined
    : normalized;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const toOptionalStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item).trim())
      .filter(Boolean);
    return normalized.length > 0 ? normalized : undefined;
  }

  return undefined;
};

const toDateKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const isoPrefixMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefixMatch?.[1]) {
    return isoPrefixMatch[1];
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
};

const getTodayDateKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const hasInvalidCelebrationDateRange = (startDate: string | undefined, endDate: string | undefined): boolean => {
  const startDateKey = toDateKey(startDate);
  const endDateKey = toDateKey(endDate);

  if (startDateKey && startDateKey < getTodayDateKey()) {
    return true;
  }

  if (startDateKey && endDateKey && endDateKey < startDateKey) {
    return true;
  }

  return false;
};

const normalizeSubtypeKey = (value: string): string => value
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

const toCanonicalSubtype = (
  celebrationType: string,
  subtypeValue: unknown,
): string | undefined => {
  const rawSubtype = toOptionalString(subtypeValue);
  if (!rawSubtype) {
    return undefined;
  }

  const normalizedSubtype = normalizeSubtypeKey(rawSubtype);
  return isValidSubtype(celebrationType, normalizedSubtype)
    ? normalizedSubtype
    : undefined;
};

export const buildCreateWorkflowInput = (
  params: BuildCreateWorkflowInputParams,
): CreateCelebrationWorkflowInput | null => {
  const draft = params.draft ?? {};

  const title = toOptionalString(draft.title);
  const celebrationType = toOptionalString(draft.celebration_type);
  if (!title || !celebrationType) {
    return null;
  }

  const startDate = toOptionalString(draft.start_date);
  const endDate = toOptionalString(draft.end_date);
  if (hasInvalidCelebrationDateRange(startDate, endDate)) {
    return null;
  }

  const celebrantNames = toOptionalStringArray(draft.celebrant_names) ?? [];
  const celebrantRoles = toOptionalStringArray(draft.celebrant_roles) ?? [];
  const celebrantEmails = toOptionalStringArray(draft.celebrant_emails) ?? [];

  const celebrants = celebrantNames.map((name, index) => ({
    name,
    role: celebrantRoles[index],
    email: celebrantEmails[index],
  }));

  return {
    celebrationData: {
      title,
      celebration_type: celebrationType,
      celebration_subtype: toCanonicalSubtype(celebrationType, draft.celebration_subtype),
      template: normalizeWorkflowTemplateKey(draft.template),
      description: toOptionalString(draft.description),
      start_date: startDate,
      end_date: endDate,
      budget: toOptionalNumber(draft.budget),
      location_name: toOptionalString(draft.location_name),
      latitude: toOptionalNumber(draft.latitude),
      longitude: toOptionalNumber(draft.longitude),
      radius_miles: toOptionalNumber(draft.radius_miles),
      rsvp_deadline: toOptionalString(draft.rsvp_deadline),
    },
    celebrants,
    paragraphContent: toOptionalString(draft.description),
    confirmedTags: params.confirmedTags,
    chatSessionId: params.chatSessionId,
    eventSelections: {
      selectedEvents: params.selectedEvents,
      selectedActivities: params.selectedActivities,
      selectedGoalTemplateIdsByEvent: params.selectedGoalTemplateIdsByEvent,
      addServicesToShortlist: params.addServicesToShortlist,
      selectedServiceDefinitionIds: params.selectedServiceDefinitionIds,
    },
  };
};
